/**
 * Admin session storage. Mirrors the backend's tenant-scoped key so
 * a hypothetical multi-tenant SPA bundle wouldn't cross-contaminate.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

const STORAGE_KEY = 'greenline_admin_token'

interface AdminSession {
  token: string
  email: string
}

interface AdminAuthContextValue {
  session: AdminSession | null
  signIn: (s: AdminSession) => void
  signOut: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

function readSession(): AdminSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AdminSession
    if (!parsed.token || !parsed.email) return null
    return parsed
  } catch {
    return null
  }
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AdminSession | null>(() => readSession())

  useEffect(() => {
    function onStorage(ev: StorageEvent) {
      if (ev.key === STORAGE_KEY) setSession(readSession())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value: AdminAuthContextValue = {
    session,
    signIn(s) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
      setSession(s)
    },
    signOut() {
      window.localStorage.removeItem(STORAGE_KEY)
      setSession(null)
    },
  }
  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used inside <AdminAuthProvider>')
  return ctx
}

export function authHeader(session: AdminSession | null): HeadersInit {
  return session ? { authorization: `Bearer ${session.token}` } : {}
}
